// src/components/CompetitiveAdvantageSection.js

import React from 'react';
import styled from 'styled-components';
import {
  FaMoneyBillWave,
  FaTrophy,
  FaShieldAlt,
  FaHandPointer,
  FaRocket  // If still needed for the top icon
} from 'react-icons/fa';

import { Section, HorizontalLine, IconWrapper } from '../common/Sections';
import { media } from '../../utils/media';

const CompetitiveAdvantage = () => {
  // New content array with 4 containers
  const venueAdvantages = [
    {
      icon: <FaMoneyBillWave />,
      title: 'Novel source of income',
      description:
        'Using emerging technologies and our proprietary software, every memory created on your courts or fields can now be purchased – generating an entirely new source of income for your business.'
    },
    {
      icon: <FaTrophy />,
      title: 'Competitive advantage',
      description:
        'Once our system is installed, players will be incentivized to return to your location where they can purchase their precious memories, as opposed to your competitors where those memories would be lost.'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Security',
      description:
        'Our camera systems provide added security and can replace your existing security setup at better costs.'
    },
    {
      icon: <FaHandPointer />,
      title: 'Easy to use',
      description:
        'Once a guest has finished their game, they simply scan our QR code. They can then purchase their video and quickly access their footage to analyze and share on social media.'
    }
  ];

  return (
    <Section id="competitive-advantage">
      <Container>
        <ContentWrapper>
          <TextContent>
            <TitleWrapper>
              {/* You can replace <FaRocket /> if you want a different icon for the top heading */}
              <IconWrapper>
                <FaRocket />
              </IconWrapper>
              <Title>For Venues</Title>
              {/* <HorizontalLine /> */}
            </TitleWrapper>
            {/* <Description>
              We stand out by providing these benefits and features specifically tailored to sports venues.
            </Description> */}
          </TextContent>

          <AdvantageGrid>
            {venueAdvantages.map((item, index) => (
              <AdvantageCard key={index}>
                <CardIcon>{item.icon}</CardIcon>
                <CardTitle>{item.title}</CardTitle>
                <CardDescription>{item.description}</CardDescription>
              </AdvantageCard>
            ))}
          </AdvantageGrid>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

export default CompetitiveAdvantage;

// ────────────────── STYLED COMPONENTS ───────────────────

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.lg};
  position: relative;
  z-index: 2;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  animation: fadeInUp 1s ease-out;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.title};
  margin: 0;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.primary},
      ${({ theme }) => theme.colors.secondary}
    );
    border-radius: 2px;
  }
`;

const Description = styled.p`
  font-size: clamp(1rem, 2vw, 1.1rem);
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0;
  position: relative;
  padding: 0 ${({ theme }) => theme.spacing.lg};

  &::before,
  &::after {
    content: '"';
    position: absolute;
    font-size: 4rem;
    color: ${({ theme }) => theme.colors.primary};
    opacity: 0.2;
  }

  &::before {
    left: 0;
    top: -20px;
  }

  &::after {
    right: 0;
    bottom: -20px;
    transform: rotate(180deg);
  }

  ${media.tablet(`
    padding: 0 ${({ theme }) => theme.spacing.md};
  `)}
`;

const AdvantageGrid = styled.div`
  display: grid;
  /* auto-fit ensures it wraps properly on smaller screens */
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const AdvantageCard = styled.div`
  background: ${({ theme }) => theme.colors.secondaryBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: fadeInUp 0.5s ease-out;
  animation-fill-mode: both;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      rgba(255, 111, 60, 0.1),
      rgba(255, 111, 60, 0.05)
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
    border-color: ${({ theme }) => theme.colors.primary}40;

    &::before {
      opacity: 1;
    }

    ${'' /* Just an example if you want the icon or text to transform */}
    ${'' /* e.g., ${CardIcon} { transform: scale(1.1) rotate(5deg); } */}
  }

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }
`;

const CardIcon = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary} 0%,
    ${({ theme }) => theme.colors.secondary} 100%
  );
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.white};
  font-size: 2rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    inset: -5px;
    border-radius: 50%;
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.primary}20,
      ${({ theme }) => theme.colors.secondary}20
    );
    z-index: -1;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.title};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  position: relative;
  z-index: 1;
`;

const CardDescription = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  margin: 0;
  position: relative;
  z-index: 1;
`;
